import { useEffect, useMemo, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { PremiumBadge, LevelBadge } from "@/components/Badges";
import {
  Eye, Inbox, Crown, Sparkles, Trophy, ArrowRight, ArrowUpRight,
  Camera, Image as ImageIcon, FileText, Heart, Check, Circle,
  CalendarDays, Clock, MapPin,
} from "lucide-react";
import AdminControlledWidgets from "@/components/AdminControlledWidgets";
import DashboardHero from "@/components/DashboardHero";
import ProfileQRCard from "@/components/ProfileQRCard";
import PointsCard from "@/components/PointsCard";
import ViewsChart from "@/components/ViewsChart";

/**
 * Talent dashboard — Simplified, action-oriented layout.
 * Sections: Hero action · 3 KPIs · Progression checklist · Concours · Quick actions · Profile preview.
 */
export default function TalentDashboard() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [concours, setConcours] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [pp, setPp] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && !user.role) navigate("/onboarding");
    if (!loading && user && user.role !== "talent") {
      if ((user.role === "creator" || user.role === "professional")) navigate("/createurs");
      else if (user.role === "admin") navigate("/admin");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role === "talent") loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAll = async () => {
    const [s, a, c, ph, pf] = await Promise.all([
      api.get("/my/stats").catch(() => ({ data: null })),
      api.get("/my/applications").catch(() => ({ data: [] })),
      api.get("/concours/current").catch(() => ({ data: null })),
      api.get(`/portfolio/${user.user_id}`).catch(() => ({ data: [] })),
      api.get(`/portfolio-profile/${user.user_id}`).catch(() => ({ data: null })),
    ]);
    setStats(s.data || {
      progress: profile?.progress ?? 0,
      level: profile?.level ?? "Débutant",
      is_premium: profile?.is_premium ?? false,
      applications: 0, accepted: 0,
      views_total: 0, views_week: 0, views_trend: 0, chart: [],
    });
    setApps(a.data || []);
    setConcours(c.data);
    setPhotos(Array.isArray(ph.data) ? ph.data : []);
    setPp(pf.data || null);
  };

  /** Build the profile-completion checklist from real data */
  const checklist = useMemo(() => {
    const photosOk = photos.filter((p) => !p.is_deleted).length >= 3;
    const bioOk = !!(pp?.bio || profile?.bio);
    const universeOk = (pp?.mood_tags?.length || 0) +
                       (pp?.favorite_styles?.length || 0) +
                       (pp?.inspirations?.length || 0) > 0;
    const signatureOk = !!(pp?.signature_quote || pp?.headline);
    return [
      { id: "photos", label: "Ajouter au moins 3 photos", done: photosOk, to: "/talent/portfolio", icon: Camera },
      { id: "bio", label: "Compléter votre bio", done: bioOk, to: "/talent/profile", icon: FileText },
      { id: "universe", label: "Définir votre univers", done: universeOk, to: "/talent/universe", icon: Sparkles },
      { id: "signature", label: "Écrire votre phrase signature", done: signatureOk, to: "/talent/profile", icon: Heart },
    ];
  }, [photos, pp, profile]);

  const checklistDone = checklist.filter((i) => i.done).length;
  const checklistTotal = checklist.length;
  const checklistPct = Math.round((checklistDone / checklistTotal) * 100);

  /** Determine the priority hero action based on profile state */
  const heroAction = useMemo(() => {
    if (!stats) return null;
    if (photos.filter((p) => !p.is_deleted).length < 3) {
      return {
        eyebrow: "Étape suivante",
        title: "Ajoutez vos premières photos",
        body: "Téléversez au moins 3 photos pour activer votre portfolio et apparaître dans les recherches.",
        cta: "Ajouter des photos",
        to: "/talent/portfolio",
        icon: Camera,
      };
    }
    if (!(pp?.bio) && !profile?.bio) {
      return {
        eyebrow: "Étape suivante",
        title: "Racontez votre univers",
        body: "Une bio inspirée multiplie par 3 les vues de profil. Présentez-vous en quelques lignes.",
        cta: "Compléter ma bio",
        to: "/talent/profile",
        icon: FileText,
      };
    }
    if (concours && !concours.has_participated) {
      return {
        eyebrow: "Concours en cours",
        title: concours.title,
        body: concours.prize ? `Récompense : ${concours.prize}` : "Postulez avant la date limite pour gagner en visibilité.",
        cta: "Participer maintenant",
        to: `/concours/${concours.id}`,
        icon: Trophy,
        image: concours.image_url,
      };
    }
    return {
      eyebrow: "Action recommandée",
      title: "Découvrez les nouvelles collaborations",
      body: "De nouvelles opportunités sont publiées chaque semaine — postulez avant les autres.",
      cta: "Voir les collaborations",
      to: "/collaborations",
      icon: Inbox,
    };
  }, [stats, photos, pp, profile, concours]);

  if (!user || !profile || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF5]" data-testid="talent-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-serif text-lg text-[#1A1A1A]">Chargement…</p>
        </div>
      </div>
    );
  }

  const firstName = user.name?.split(" ")[0];
  const mainPhoto = photos.find((p) => p.is_main && !p.is_deleted) || photos.find((p) => !p.is_deleted) || null;
  const mainPhotoSrc = optimizeUrl(mainPhoto ? `${API}/files/${mainPhoto.storage_path}` : profile?.demo_photo_url, 640);

  return (
    <DashboardLayout progress={checklistPct}>
      <DashboardHero />
      <AdminControlledWidgets />
      {/* ============== HEADER ============== */}
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4" data-testid="talent-dash-overview">
        <div>
          <p className="caption-label text-[#C68E59]">Tableau de bord</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1 leading-[1.05]">
            Bonjour, <span className="italic">{firstName}</span>
          </h1>
          <p className="text-[#666] mt-2 text-sm">
            Voici l'essentiel à faire aujourd'hui.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {profile.is_premium ? <PremiumBadge /> : <LevelBadge level={stats.level} />}
        </div>
      </div>

      {/* ============== HERO ACTION ============== */}
      {heroAction && (
        <div
          className="relative mb-10 rounded-3xl overflow-hidden bg-[#1A1A1A] text-white shadow-[0_20px_60px_-30px_rgba(0,0,0,0.5)]"
          data-testid="hero-action"
        >
          {heroAction.image ? (
            <>
              <img src={heroAction.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
              <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/55 to-black/30" />
            </>
          ) : (
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,#3a2412,transparent_60%)]" />
          )}
          <div className="relative p-8 sm:p-10 lg:p-12 grid sm:grid-cols-3 gap-8 items-center">
            <div className="sm:col-span-2">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#E6C29A]">
                <heroAction.icon className="w-3.5 h-3.5" /> {heroAction.eyebrow}
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
                {heroAction.title}
              </h2>
              <p className="text-white/80 text-sm sm:text-base mt-4 max-w-xl leading-relaxed">
                {heroAction.body}
              </p>
              <Link to={heroAction.to} className="inline-flex mt-6">
                <Button
                  className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-7 h-12 text-sm font-semibold"
                  data-testid="hero-action-btn"
                >
                  {heroAction.cta} <ArrowUpRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
            <div className="hidden sm:flex items-center justify-end">
              <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-white/8 border border-white/15 flex items-center justify-center backdrop-blur-sm">
                <heroAction.icon className="w-12 h-12 lg:w-16 lg:h-16 text-[#E6C29A]" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============== 4 KPIs ============== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="kpi-row">
        <KPI
          icon={Eye}
          label="Vues (semaine)"
          value={stats.views_week}
          sub={`${stats.views_trend >= 0 ? "+" : ""}${stats.views_trend}% vs semaine dernière`}
          trend={stats.views_trend}
          testid="kpi-views"
        />
        <KPI
          icon={Inbox}
          label="Candidatures"
          value={stats.applications}
          sub={stats.accepted > 0 ? `${stats.accepted} acceptée${stats.accepted > 1 ? "s" : ""}` : "Aucune acceptée pour l'instant"}
          testid="kpi-apps"
        />
        <KPI
          icon={Trophy}
          label="Points Helencia"
          value={stats.points ?? 0}
          sub={stats.points_level?.next ? `Niveau ${stats.points_level.name} · ${stats.points_level.next} à ${stats.points_level.next_at} pts` : `Niveau ${stats.points_level?.name || "Élite"}`}
          testid="kpi-points"
        />
        <KPI
          icon={Crown}
          label="Statut"
          value={profile.is_premium ? "Premium" : "Free"}
          sub={profile.is_premium ? "Avantages premium actifs" : "Passez Premium pour plus de visibilité"}
          gold={profile.is_premium}
          testid="kpi-status"
          to={profile.is_premium ? null : "/talent/premium"}
        />
      </div>

      {/* ============== ROW: Points + Views chart ============== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10" data-testid="stats-row">
        <div className="lg:col-span-7">
          <PointsCard points={stats.points} level={stats.points_level} breakdown={stats.points_breakdown} />
        </div>
        <div className="lg:col-span-5">
          <ViewsChart chart={stats.chart} total={stats.views_total} week={stats.views_week} trend={stats.views_trend}
            subtitle={stats.favorited_by > 0 ? `${stats.favorited_by} marque${stats.favorited_by > 1 ? "s" : ""} vous suivent` : undefined} />
        </div>
      </div>

      {/* ============== QR CARD ============== */}
      <div className="mb-10">
        <ProfileQRCard userId={user.user_id} role="talent" name={user.name} />
      </div>

      {/* ============== ROW: Progression + Profile preview ============== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
        {/* Progression checklist */}
        <div className="lg:col-span-7 bg-white rounded-3xl border border-[#EADDC9] p-7 sm:p-8" data-testid="progression-card">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <p className="caption-label text-[#C68E59]">Progression</p>
              <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">
                {checklistDone === checklistTotal ? "Profil complet ✨" : "Complétez votre profil"}
              </h3>
            </div>
            <div className="text-right">
              <div className="font-serif text-3xl text-[#1A1A1A] leading-none">{checklistDone}<span className="text-[#8B8680] text-xl">/{checklistTotal}</span></div>
              <p className="text-[10px] uppercase tracking-wider text-[#A26F3E] mt-1">étapes</p>
            </div>
          </div>

          {/* Visual progress bar (segmented) */}
          <div className="grid gap-1.5 mb-6" style={{ gridTemplateColumns: `repeat(${checklistTotal}, 1fr)` }}>
            {checklist.map((it) => (
              <div key={it.id} className={`h-1.5 rounded-full ${it.done ? "bg-[#C68E59]" : "bg-[#FAF3EA]"}`} />
            ))}
          </div>

          {/* Checklist items */}
          <ul className="divide-y divide-[#FAF3EA]">
            {checklist.map((it) => (
              <li key={it.id}>
                <Link
                  to={it.to}
                  className="flex items-center justify-between py-3 group hover:px-2 transition-all"
                  data-testid={`checklist-${it.id}`}
                >
                  <div className="flex items-center gap-3">
                    {it.done ? (
                      <div className="w-7 h-7 rounded-full bg-[#C68E59] text-white flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </div>
                    ) : (
                      <div className="w-7 h-7 rounded-full border-2 border-[#EADDC9] flex items-center justify-center">
                        <Circle className="w-3 h-3 text-[#EADDC9]" />
                      </div>
                    )}
                    <span className={`text-sm ${it.done ? "text-[#8B8680] line-through" : "text-[#1A1A1A] font-medium"}`}>
                      {it.label}
                    </span>
                  </div>
                  {!it.done && (
                    <ArrowRight className="w-4 h-4 text-[#8B8680] group-hover:text-[#C68E59] transition" />
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Profile preview */}
        <div className="lg:col-span-5 rounded-3xl overflow-hidden bg-white border border-[#EADDC9]" data-testid="profile-preview">
          <div className="aspect-[4/5] bg-[#FAF3EA] relative">
            {mainPhotoSrc ? (
              <img src={mainPhotoSrc} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-12 h-12 text-[#B3A78C]" />
              </div>
            )}
            <div className="absolute top-3 right-3 flex flex-wrap gap-1.5">
              {profile.is_premium ? <PremiumBadge /> : <LevelBadge level={stats.level} />}
            </div>
          </div>
          <div className="p-6">
            <p className="caption-label text-[#A26F3E]">Aperçu profil public</p>
            <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1 truncate">{user.name}</h3>
            {(pp?.headline || profile.talent_type) && (
              <p className="text-sm text-[#666] mt-1 truncate">{pp?.headline || profile.talent_type}</p>
            )}
            {profile.location && (
              <p className="text-xs text-[#8B8680] mt-2 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {profile.location}
              </p>
            )}
            <Link to={`/talents/${user.user_id}`} target="_blank" className="block mt-5">
              <Button variant="outline" className="w-full rounded-full border-[#EADDC9]" data-testid="preview-public-btn">
                <Eye className="w-4 h-4 mr-2" /> Voir mon profil public
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* ============== CONCOURS ============== */}
      {concours && (
        <div
          className="mb-10 bg-gradient-to-br from-[#1A1A1A] via-[#222] to-[#1A1A1A] rounded-3xl overflow-hidden grid grid-cols-1 md:grid-cols-5"
          data-testid="concours-card"
        >
          {concours.image_url && (
            <div className="md:col-span-2 h-56 md:h-auto">
              <img src={concours.image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="md:col-span-3 p-8 md:p-10 text-white flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-4 h-4 text-[#C68E59]" />
              <span className="caption-label text-[#C68E59]">Concours actif</span>
            </div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-3">{concours.title}</h3>
            {concours.description && (
              <p className="text-white/70 text-sm leading-relaxed mb-5 max-w-xl line-clamp-2">
                {concours.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-xs text-white/80 mb-6">
              {concours.prize && (
                <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-[#C68E59]" />{concours.prize}</span>
              )}
              {concours.end_date && (
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  Jusqu'au {new Date(concours.end_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                </span>
              )}
            </div>
            <div>
              <Link to={`/concours/${concours.id}`}>
                <Button className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-6 h-11" data-testid="concours-cta">
                  {concours.has_participated ? "Voir les participations" : "Participer au concours"}
                  <ArrowUpRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ============== QUICK ACTIONS ============== */}
      <div className="mb-4">
        <p className="caption-label text-[#A26F3E]">Actions rapides</p>
        <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">Continuer où vous étiez</h3>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="quick-actions">
        <QuickAction icon={Camera}    label="Ajouter une photo"     to="/talent/portfolio"      testid="qa-photo" />
        <QuickAction icon={Inbox}     label="Voir les collaborations" to="/collaborations"      testid="qa-collabs" />
        <QuickAction icon={Trophy}    label="Participer au concours" to={concours ? `/concours/${concours.id}` : "/talent/concours"} testid="qa-concours" />
        <QuickAction icon={FileText}  label="Modifier mon profil"   to="/talent/profile"        testid="qa-profile" />
      </div>

      {/* ============== Footer info — recent applications (compact) ============== */}
      {apps.length > 0 && (
        <div className="bg-white rounded-3xl border border-[#EADDC9] p-6 sm:p-7 mb-2" data-testid="recent-apps-card">
          <div className="flex items-center justify-between mb-4">
            <p className="caption-label text-[#A26F3E]">Mes candidatures récentes</p>
            <Link to="/talent/applications" className="text-xs text-[#1A1A1A] hover:underline inline-flex items-center gap-1">
              Tout voir <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {apps.slice(0, 3).map((a) => (
              <Link key={a.id} to={`/collaborations/${a.collaboration_id}`} className="block rounded-xl border border-[#FAF3EA] p-3 hover:bg-[#FFFBF5] transition">
                <p className="text-sm font-semibold text-[#1A1A1A] truncate">{a.collaboration?.title || "Collaboration"}</p>
                <p className="text-[11px] text-[#8B8680] capitalize mt-0.5">{a.status}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

/* ============================== SUBCOMPONENTS ============================== */

function KPI({ icon: Icon, label, value, sub, trend, gold, testid, to }) {
  const positive = trend === undefined ? null : trend >= 0;
  const cls = `relative rounded-3xl p-6 border transition ${
    gold
      ? "bg-gradient-to-br from-[#FAF3EA] to-[#F5E6D0] border-[#E6C29A]"
      : "bg-white border-[#EADDC9]"
  } ${to ? "hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:border-[#C68E59]/40" : ""}`;
  const inner = (
    <>
      <div className="flex items-center justify-between mb-3">
        <span className="caption-label text-[#A26F3E]">{label}</span>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${gold ? "bg-white" : "bg-[#FAF3EA]"}`}>
          <Icon className={`w-4 h-4 ${gold ? "text-[#C68E59]" : "text-[#A26F3E]"}`} />
        </div>
      </div>
      <div className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] leading-none mt-1">
        {value}
      </div>
      {sub && (
        <p className={`text-xs mt-3 leading-relaxed ${positive === null ? "text-[#8B8680]" : positive ? "text-[#2E7D32]" : "text-[#A3483F]"}`}>
          {sub}
        </p>
      )}
    </>
  );
  return to ? (
    <Link to={to} className={cls} data-testid={testid}>{inner}</Link>
  ) : (
    <div className={cls} data-testid={testid}>{inner}</div>
  );
}

function QuickAction({ icon: Icon, label, to, testid }) {
  return (
    <Link
      to={to}
      className="group flex flex-col items-start justify-between rounded-3xl bg-white border border-[#EADDC9] p-5 sm:p-6 h-full hover:border-[#C68E59]/50 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] transition-all"
      data-testid={testid}
    >
      <div className="w-11 h-11 rounded-full bg-[#FAF3EA] flex items-center justify-center group-hover:bg-[#C68E59] transition-colors">
        <Icon className="w-5 h-5 text-[#A26F3E] group-hover:text-white transition-colors" />
      </div>
      <p className="font-serif text-base sm:text-lg text-[#1A1A1A] mt-5 leading-tight">{label}</p>
      <ArrowRight className="w-4 h-4 text-[#8B8680] mt-3 group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}
